import { useState, useEffect } from "react";
import { Gift, Bell, UserPlus, Check, Trophy } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/i18n";
import { useAuth } from "@/auth";
import { api } from "@/lib/api";
import { pushSupported, isPushSubscribed, enablePush } from "@/lib/push";

// Reusable "win weekly sponsor gifts" call-to-action: sign up + enable push.
// Used on the Save-to-Phone popup and the post-game result screen.
export default function WeeklyGiftsCta({ onSignUp, prefix = "gifts", className = "" }) {
  const { t } = useLang();
  const { user } = useAuth();
  const [notifsOn, setNotifsOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [competing, setCompeting] = useState(0);
  useEffect(() => { isPushSubscribed().then(setNotifsOn); }, []);
  useEffect(() => { api.tournamentCompeting().then((r) => setCompeting(r.data.count || 0)).catch(() => {}); }, []);

  const allowNotifs = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await enablePush(user?.id);
      if (r.ok) { setNotifsOn(true); toast.success(t("remindersEnabled")); }
      else if (r.reason === "denied") toast.error(t("remindersDenied"));
      else toast.message(t("remindersUnsupported"));
    } catch { toast.error("Error"); }
    finally { setBusy(false); }
  };

  return (
    <div className={`rounded-2xl bg-[#1c1505] border border-[#FFB300]/45 p-4 ${className}`} data-testid={`${prefix}-gifts-cta`}>
      <div className="flex items-center gap-2">
        <Gift size={18} strokeWidth={2.5} className="text-[#FFB300]" />
        <h3 className="font-pixel text-[11px] text-[#FFB300] leading-snug">{t("giftsTitle")}</h3>
      </div>
      <p className="font-vt text-base text-white/85 leading-tight mt-1.5">{t("giftsBody")}</p>
      {competing > 0 && (
        <div data-testid={`${prefix}-competing-hook`} className="flex items-center gap-1.5 mt-2 font-vt text-base text-[#FFD54F]">
          <Trophy size={14} strokeWidth={2.5} /> {competing} {t("competingForGift")}
        </div>
      )}
      <div className="flex flex-col gap-2 mt-3">
        {user ? (
          <div data-testid={`${prefix}-signedin`} className="flex items-center gap-2 rounded-xl bg-[#0d2a12] border border-[#4CAF50]/40 px-4 py-2.5 font-vt text-base text-[#9be59b]">
            <Check size={16} strokeWidth={3} /> {t("giftsSignedIn")}
          </div>
        ) : (
          <button data-testid={`${prefix}-signup-btn`} onClick={() => onSignUp && onSignUp()}
            className="pixel-btn bg-[#0067C6] text-white px-4 py-3 text-[11px] flex items-center justify-center gap-2">
            <UserPlus size={16} strokeWidth={2.5} /> {t("giftsSignup")}
          </button>
        )}
        {pushSupported() && (notifsOn ? (
          <div data-testid={`${prefix}-notifs-on`} className="flex items-center gap-2 rounded-xl bg-[#0d2a12] border border-[#4CAF50]/40 px-4 py-2.5 font-vt text-base text-[#9be59b]">
            <Check size={16} strokeWidth={3} /> {t("notifsOn")}
          </div>
        ) : (
          <button data-testid={`${prefix}-allow-notifs-btn`} onClick={allowNotifs} disabled={busy}
            className="pixel-btn bg-[#FFB300] text-black px-4 py-3 text-[11px] flex items-center justify-center gap-2 disabled:opacity-50">
            <Bell size={16} strokeWidth={2.5} /> {busy ? "..." : t("allowNotifs")}
          </button>
        ))}
      </div>
    </div>
  );
}
